import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import _settings from '../services/settings.service.js';

const CONTACT_EMAIL = 'contact@gotasoft.io';
const LAST_UPDATED = '10 de julio de 2026';

export default function PrivacyPolicyScreen() {
	const [companyName, setCompanyName] = useState('');

	useEffect(() => {
		const fetchCompanyName = async () => {
			try {
				const { data } = await _settings.listPublic();
				setCompanyName(data?.companyName || '');
			} catch (err) {
				// El nombre de la empresa solo se usa como texto; la página funciona igual sin él.
			}
		};
		fetchCompanyName();
	}, []);

	const appName = companyName || 'la Aplicación';
	const companyLabel = companyName || 'la Empresa';

	return (
		<div className='container mt-4 mb-5' style={{ maxWidth: '900px' }}>
			<Helmet title='Política de Privacidad' />
			<h1>Política de Privacidad</h1>
			<p><em>Última actualización: {LAST_UPDATED}</em></p>

			<p>
				Esta Política de Privacidad describe Nuestras políticas y procedimientos sobre la recopilación, uso y
				divulgación de Su información cuando Usted usa el Servicio, y le informa sobre Sus derechos de privacidad y
				cómo la ley Lo protege.
			</p>
			<p>
				Usamos Sus datos personales para proporcionar y mejorar el Servicio. Al usar el Servicio, Usted acepta la
				recopilación y el uso de información de acuerdo con esta Política de Privacidad.
			</p>

			<h2>Interpretación y Definiciones</h2>
			<h3>Interpretación</h3>
			<p>
				Las palabras cuya letra inicial está en mayúscula tienen significados definidos bajo las siguientes
				condiciones. Las siguientes definiciones tendrán el mismo significado sin importar si aparecen en singular
				o en plural.
			</p>

			<h3>Definiciones</h3>
			<p>Para los propósitos de esta Política de Privacidad:</p>
			<ul>
				<li><strong>Cuenta</strong> significa una cuenta única creada para que Usted acceda a nuestro Servicio o partes de nuestro Servicio.</li>
				<li>
					<strong>Afiliada</strong> significa una entidad que controla, es controlada por, o está bajo control
					común con una parte, donde &quot;control&quot; significa la propiedad del 50% o más de las acciones,
					participación accionaria u otros valores con derecho a voto para la elección de directores u otra
					autoridad administrativa.
				</li>
				<li><strong>Aplicación</strong> se refiere a {appName}, el programa de software provisto por la Empresa.</li>
				<li>
					<strong>Empresa</strong> (referida como &quot;la Empresa&quot;, &quot;Nosotros&quot; o
					&quot;Nuestro/a&quot; en este Acuerdo) se refiere a {companyLabel}.
				</li>
				<li><strong>Dispositivo</strong> significa cualquier dispositivo que pueda acceder al Servicio, tal como una computadora, un celular o una tablet digital.</li>
				<li><strong>Datos Personales</strong> es cualquier información relacionada con una persona identificada o identificable.</li>
				<li><strong>Servicio</strong> se refiere a la Aplicación.</li>
				<li>
					<strong>Proveedor de Servicios</strong> significa cualquier persona física o jurídica que procese los
					datos en nombre de la Empresa. Se refiere a empresas o personas externas contratadas por la Empresa para
					facilitar el Servicio, proveer el Servicio en nombre de la Empresa, realizar servicios relacionados con
					el Servicio, o asistir a la Empresa en el análisis de cómo se usa el Servicio.
				</li>
				<li>
					<strong>Datos de Uso</strong> se refiere a datos recopilados automáticamente, ya sea generados por el
					uso del Servicio o por la propia infraestructura del Servicio (por ejemplo, la duración de una visita a
					una página).
				</li>
				<li>
					<strong>Usted</strong> significa la persona que accede o usa el Servicio, o la empresa u otra entidad
					legal en cuyo nombre dicha persona accede o usa el Servicio, según corresponda.
				</li>
			</ul>

			<h2>Recopilación y Uso de Sus Datos Personales</h2>
			<h3>Tipos de Datos Recopilados</h3>
			<h4>Datos Personales</h4>
			<p>
				Mientras usa Nuestro Servicio, podemos pedirle que Nos proporcione cierta información de identificación
				personal que pueda usarse para contactarlo o identificarlo. La información de identificación personal
				puede incluir, sin limitarse a:
			</p>
			<ul>
				<li>Dirección de email</li>
				<li>Nombre y apellido</li>
				<li>Número de teléfono</li>
				<li>Datos de Uso</li>
			</ul>

			<h4>Datos de Uso</h4>
			<p>Los Datos de Uso se recopilan automáticamente al usar el Servicio.</p>
			<p>
				Los Datos de Uso pueden incluir información tal como la dirección de Protocolo de Internet de Su
				Dispositivo (por ejemplo, dirección IP), tipo de navegador, versión del navegador, las páginas de nuestro
				Servicio que Usted visita, la hora y fecha de Su visita, el tiempo dedicado a esas páginas, identificadores
				únicos de dispositivo y otros datos de diagnóstico.
			</p>
			<p>
				Cuando Usted accede al Servicio por o a través de un dispositivo móvil, podemos recopilar cierta
				información automáticamente, incluyendo, sin limitarse a, el tipo de dispositivo móvil que usa, el
				identificador único de Su dispositivo móvil, la dirección IP de Su dispositivo móvil, Su sistema operativo
				móvil, el tipo de navegador de Internet móvil que usa, identificadores únicos de dispositivo y otros datos
				de diagnóstico.
			</p>
			<p>También podemos recopilar la información que Su navegador envía cada vez que visita nuestro Servicio o cuando accede al Servicio por o a través de un dispositivo móvil.</p>

			<h3>Uso de Sus Datos Personales</h3>
			<p>La Empresa puede usar los Datos Personales para los siguientes propósitos:</p>
			<ul>
				<li>Para proveer y mantener nuestro Servicio, incluyendo monitorear el uso de nuestro Servicio.</li>
				<li>
					Para administrar Su Cuenta: para gestionar Su registro como usuario del Servicio. Los Datos Personales
					que Usted proporciona pueden darle acceso a distintas funcionalidades del Servicio disponibles para
					Usted como usuario registrado.
				</li>
				<li>
					Para la ejecución de un contrato: el desarrollo, cumplimiento y ejecución del contrato de compra de los
					productos, artículos o servicios que Usted haya adquirido, o de cualquier otro contrato con Nosotros a
					través del Servicio.
				</li>
				<li>
					Para contactarlo: Para contactarlo por email, llamadas telefónicas, SMS u otras formas equivalentes de
					comunicación electrónica, como notificaciones push de una aplicación móvil, respecto de actualizaciones
					o comunicaciones informativas relacionadas con las funcionalidades, productos o servicios contratados,
					incluyendo actualizaciones de seguridad, cuando sea necesario o razonable para su implementación.
				</li>
				<li>
					Para proporcionarle noticias, ofertas especiales e información general sobre otros bienes, servicios y
					eventos que ofrecemos y que sean similares a los que ya haya adquirido o consultado, a menos que Usted
					haya optado por no recibir dicha información.
				</li>
				<li>Para gestionar Sus solicitudes: Para atender y gestionar Sus solicitudes hacia Nosotros.</li>
				<li>
					Para transferencias de negocio: Podemos usar Su información para evaluar o llevar a cabo una fusión,
					desinversión, reestructuración, reorganización, disolución u otra venta o transferencia de parte o la
					totalidad de Nuestros activos, ya sea como negocio en marcha o como parte de un proceso de quiebra,
					liquidación o similar, en el cual los Datos Personales que mantenemos sobre los usuarios de nuestro
					Servicio se encuentren entre los activos transferidos.
				</li>
				<li>
					Para otros propósitos: Podemos usar Su información para otros propósitos, tales como análisis de datos,
					identificación de tendencias de uso, determinación de la efectividad de nuestras campañas
					promocionales, y para evaluar y mejorar nuestro Servicio, productos, servicios, marketing y su
					experiencia.
				</li>
			</ul>

			<p>Podemos compartir Su información personal en las siguientes situaciones:</p>
			<ul>
				<li>Con Proveedores de Servicios: Podemos compartir Su información personal con Proveedores de Servicios para monitorear y analizar el uso de nuestro Servicio, y para contactarlo.</li>
				<li>Para transferencias de negocio: Podemos compartir o transferir Su información personal en relación con, o durante negociaciones de, cualquier fusión, venta de activos de la Empresa, financiamiento, o adquisición de la totalidad o parte de Nuestro negocio por otra empresa.</li>
				<li>Con Afiliadas: Podemos compartir Su información con Nuestras afiliadas, en cuyo caso exigiremos a dichas afiliadas que respeten esta Política de Privacidad. Las Afiliadas incluyen a Nuestra empresa matriz y cualquier otra subsidiaria, socios de joint venture u otras empresas que controlemos o que estén bajo control común con Nosotros.</li>
				<li>Con socios comerciales: Podemos compartir Su información con Nuestros socios comerciales para ofrecerle ciertos productos, servicios o promociones.</li>
				<li>Con otros usuarios: cuando Usted comparte información personal o de otra forma interactúa en las áreas públicas con otros usuarios, dicha información puede ser vista por todos los usuarios y puede distribuirse públicamente hacia el exterior.</li>
				<li>Con Su consentimiento: Podemos divulgar Su información personal para cualquier otro propósito con Su consentimiento.</li>
			</ul>

			<h3>Retención de Sus Datos Personales</h3>
			<p>
				La Empresa retendrá Sus Datos Personales solo durante el tiempo necesario para los propósitos establecidos
				en esta Política de Privacidad. Retendremos y usaremos Sus Datos Personales en la medida necesaria para
				cumplir con nuestras obligaciones legales (por ejemplo, si debemos retener sus datos para cumplir con las
				leyes aplicables), resolver disputas y hacer cumplir nuestros acuerdos y políticas legales.
			</p>
			<p>
				La Empresa también retendrá los Datos de Uso para fines de análisis interno. Los Datos de Uso generalmente
				se retienen por un período de tiempo más corto, excepto cuando estos datos se usan para fortalecer la
				seguridad o mejorar la funcionalidad de Nuestro Servicio, o cuando estamos legalmente obligados a retener
				estos datos por períodos de tiempo más largos.
			</p>

			<h3>Transferencia de Sus Datos Personales</h3>
			<p>
				Su información, incluyendo los Datos Personales, se procesa en las oficinas operativas de la Empresa y en
				cualquier otro lugar donde se encuentren las partes involucradas en el procesamiento. Esto significa que
				dicha información puede transferirse a, y mantenerse en, computadoras ubicadas fuera de Su estado,
				provincia, país u otra jurisdicción gubernamental donde las leyes de protección de datos puedan diferir de
				las de Su jurisdicción.
			</p>
			<p>Su consentimiento a esta Política de Privacidad seguido del envío de dicha información representa Su acuerdo con dicha transferencia.</p>
			<p>
				La Empresa tomará todas las medidas razonablemente necesarias para garantizar que Sus datos sean tratados
				de forma segura y de acuerdo con esta Política de Privacidad, y no se realizará ninguna transferencia de
				Sus Datos Personales a una organización o país a menos que existan controles adecuados, incluyendo la
				seguridad de Sus datos y otra información personal.
			</p>

			<h3>Eliminación de Sus Datos Personales</h3>
			<p>Usted tiene derecho a eliminar o solicitar que lo asistamos en eliminar los Datos Personales que hemos recopilado sobre Usted.</p>
			<p>Nuestro Servicio puede darle la posibilidad de eliminar cierta información sobre Usted desde dentro del Servicio.</p>
			<p>
				Usted puede actualizar, modificar o eliminar Su información en cualquier momento iniciando sesión en Su
				Cuenta, si tiene una, y visitando la sección de configuración de la cuenta que le permite administrar Su
				información personal. También puede contactarnos para solicitar acceso, corrección o eliminación de
				cualquier información personal que Usted nos haya proporcionado.
			</p>
			<p>Tenga en cuenta, sin embargo, que podemos necesitar retener cierta información cuando tengamos una obligación legal o base legítima para hacerlo.</p>

			<h3>Divulgación de Sus Datos Personales</h3>
			<h4>Transacciones Comerciales</h4>
			<p>
				Si la Empresa se ve involucrada en una fusión, adquisición o venta de activos, Sus Datos Personales pueden
				ser transferidos. Le notificaremos antes de que Sus Datos Personales sean transferidos y queden sujetos a
				una Política de Privacidad diferente.
			</p>
			<h4>Cumplimiento de la ley</h4>
			<p>
				Bajo ciertas circunstancias, la Empresa puede estar obligada a divulgar Sus Datos Personales si así lo
				requiere la ley o en respuesta a solicitudes válidas de autoridades públicas (por ejemplo, un tribunal o
				una agencia gubernamental).
			</p>
			<h4>Otros requisitos legales</h4>
			<p>La Empresa puede divulgar Sus Datos Personales de buena fe cuando considere que dicha acción es necesaria para:</p>
			<ul>
				<li>Cumplir con una obligación legal</li>
				<li>Proteger y defender los derechos o la propiedad de la Empresa</li>
				<li>Prevenir o investigar posibles irregularidades en relación con el Servicio</li>
				<li>Proteger la seguridad personal de los Usuarios del Servicio o del público</li>
				<li>Protegerse contra responsabilidad legal</li>
			</ul>

			<h3>Seguridad de Sus Datos Personales</h3>
			<p>
				La seguridad de Sus Datos Personales es importante para Nosotros, pero recuerde que ningún método de
				transmisión por Internet, ni método de almacenamiento electrónico, es 100% seguro. Aunque nos esforzamos
				por usar medios comercialmente aceptables para proteger Sus Datos Personales, no podemos garantizar su
				seguridad absoluta.
			</p>

			<h2>Privacidad de los Niños</h2>
			<p>
				Nuestro Servicio no está dirigido a personas menores de 13 años. No recopilamos a sabiendas información
				de identificación personal de personas menores de 13 años. Si Usted es padre, madre o tutor y tiene
				conocimiento de que su hijo/a nos ha proporcionado Datos Personales, por favor contáctenos. Si tomamos
				conocimiento de que hemos recopilado Datos Personales de alguien menor de 13 años sin verificación del
				consentimiento parental, tomamos medidas para eliminar esa información de Nuestros servidores.
			</p>
			<p>
				Si necesitamos basarnos en el consentimiento como base legal para procesar Su información y Su país
				requiere el consentimiento de un padre, podemos requerir el consentimiento de Su padre/madre antes de
				recopilar y usar esa información.
			</p>

			<h2>Enlaces a Otros Sitios Web</h2>
			<p>
				Nuestro Servicio puede contener enlaces a otros sitios web que no son operados por Nosotros. Si Usted hace
				clic en un enlace de un tercero, será dirigido al sitio de ese tercero. Le recomendamos encarecidamente que
				revise la Política de Privacidad de cada sitio que visite.
			</p>
			<p>No tenemos control sobre, ni asumimos responsabilidad por, el contenido, las políticas de privacidad o las prácticas de sitios o servicios de terceros.</p>

			<h2>Cambios a esta Política de Privacidad</h2>
			<p>Podemos actualizar Nuestra Política de Privacidad de vez en cuando. Le notificaremos de cualquier cambio publicando la nueva Política de Privacidad en esta página.</p>
			<p>
				Le informaremos por email y/o mediante un aviso destacado en Nuestro Servicio, antes de que el cambio
				entre en vigencia, y actualizaremos la fecha de &quot;Última actualización&quot; en la parte superior de
				esta Política de Privacidad.
			</p>
			<p>Se recomienda revisar esta Política de Privacidad periódicamente para conocer cualquier cambio. Los cambios a esta Política de Privacidad son efectivos cuando se publican en esta página.</p>

			<h2>Contáctenos</h2>
			<p>Si tiene alguna pregunta sobre esta Política de Privacidad, puede contactarnos:</p>
			<ul>
				<li>Por email: {CONTACT_EMAIL}</li>
			</ul>
		</div>
	);
}
